import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Presentation, BookOpen, TrendingUp, Scale, Gauge, ArrowRight,
  ShieldCheck, Landmark, ScrollText, Receipt, PieChart,
  FileSearch, LineChart, BadgeCheck, Map, Handshake, HardHat,
} from "lucide-react";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { SectionEyebrow } from "../components/landing/SectionEyebrow";
import { BUSINESS_DOCS, docSlug } from "../lib/documentGuides";



const PROJECT_DOCS = [
  {
    n: "01",
    Icon: FileSearch,
    title: "Feasibility Study",
    what: "An independent technical and market assessment of your project — demand, costs, timelines, technology and risks — done before serious money moves.",
    funders: "It's the anchor document of project finance. Lenders and DFIs fund projects that work on paper first; a bankable feasibility study is what turns your idea into an assessable investment.",
    you: "It catches fatal flaws while they're still cheap to fix — before you've broken ground or committed millions. Many projects die in diligence for problems a feasibility study would have surfaced early.",
  },
  {
    n: "02",
    Icon: LineChart,
    title: "Project Financial Model",
    what: "A model covering the build phase and years of operations — construction costs, ramp-up, revenues, debt service and sensitivity scenarios.",
    funders: "Project financiers live in the model: they test your debt service coverage, stress your assumptions and check the downside cases. A model that survives that scrutiny is what gets a term sheet.",
    you: "It tells you how much debt the project can truly carry, when equity runs out, and which assumptions make or break your returns — before you sign anything.",
  },
  {
    n: "03",
    Icon: BadgeCheck,
    title: "Permits, Licences & EIA",
    what: "Construction permits, sector licences and environmental & social impact approvals (EIA/ESIA) for the project.",
    funders: "No lender funds an unpermitted project — approvals are what de-risk the construction timeline. DFIs in particular will not move without environmental and social sign-off.",
    you: "Sequencing approvals early keeps your timeline honest. A stalled permit mid-construction burns cash faster than almost any other project risk.",
  },
  {
    n: "04",
    Icon: Map,
    title: "Land & Asset Documentation",
    what: "Title deeds, leases, surveys and agreements proving you control the site or assets the project is built on.",
    funders: "The security package starts with the land. Clean, undisputed title is non-negotiable — it's often the collateral that anchors the whole financing.",
    you: "Resolving boundary or ownership questions before diligence saves months. A disputed title discovered late can unwind an otherwise done deal.",
  },
  {
    n: "05",
    Icon: Handshake,
    title: "Offtake & Pre-sale Agreements",
    what: "Signed commitments from future buyers — power purchase agreements, pre-let leases, off-plan sales, supply contracts.",
    funders: "This is the future revenue funders can bank on. A project with contracted demand is a fundamentally different risk from one built on hope — and it prices accordingly.",
    you: "Every signed offtake lowers your cost of capital and strengthens your negotiating position. Selling before you build is the oldest de-risking tool in the book.",
  },
  {
    n: "06",
    Icon: HardHat,
    title: "Sponsor & Contractor Track Record",
    what: "Your team's delivery history, plus the construction contracts behind the build — EPC agreements, bills of quantities, warranties.",
    funders: "Most projects fail in execution, not on paper. Credible builders and fixed-price contracts shift construction risk away from the financier — which is exactly what gets them comfortable.",
    you: "The right contracts move cost-overrun risk off your shoulders and onto the contractor's. That single negotiation can decide whether your equity survives the build.",
  },
];

const Block = ({ label, text }) => (
  <div className="mt-4">
    <p className="text-[11px] font-bold uppercase tracking-widest text-[#0A8B85]">{label}</p>
    <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{text}</p>
  </div>
);

const DocCard = ({ n, Icon, title, what, funders, you, testPrefix, accent }) => (
  <article
    id={docSlug(title)}
    data-testid={`${testPrefix}-${n}`}
    className="scroll-mt-24 rounded-2xl border border-neutral-200 bg-white p-6 shadow-[0_20px_50px_-30px_rgba(10,139,133,0.25)] sm:p-8"
  >
    <div className="flex items-start gap-4">
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ring-1 ${accent ? "bg-[#D1A330]/10 ring-[#D1A330]/25" : "bg-[#0A8B85]/10 ring-[#0A8B85]/20"}`}>
        <Icon className={`h-5 w-5 ${accent ? "text-[#D1A330]" : "text-[#0A8B85]"}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-extrabold tracking-widest text-[#F59E0B]">{n}</p>
        <h3 style={{ fontFamily: "'Raleway', sans-serif" }} className="mt-0.5 text-lg font-bold tracking-tight text-neutral-900 md:text-xl">
          {title}
        </h3>
      </div>
    </div>
    <div className="mt-2 grid gap-x-8 sm:grid-cols-3">
      <Block label="What it is" text={what} />
      <Block label="Why funders want it" text={funders} />
      <Block label="Why it matters to you" text={you} />
    </div>
  </article>
);

export default function WhyDocumentsMatter() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "The Documents That Open Doors | Clinq";
    const target = window.location.hash ? document.getElementById(window.location.hash.slice(1)) : null;
    if (target) {
      const t = setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="why-documents-page" className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-black px-4 pb-16 pt-14 text-white sm:px-6 sm:pb-20 sm:pt-20">
        <div className="mx-auto max-w-4xl text-center">
          <h1
            data-testid="why-docs-hero-title"
            style={{ fontFamily: "'Raleway', sans-serif" }}
            className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            The Documents That <span className="text-[#2DD4BF]">Open Doors</span>
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-white/75">
            You can have a great business and still hear &ldquo;no&rdquo; from every funder you meet.
            Most of the time, it&apos;s not because your business isn&apos;t good enough. It&apos;s because
            you couldn&apos;t show it. Funders don&apos;t invest in stories — they invest in evidence.
            These documents are your evidence — for your business, and for the projects you build.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <a href="#business-docs" data-testid="why-docs-jump-business"
              className="rounded-full border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 px-4 py-1.5 text-sm font-bold text-[#2DD4BF] transition-colors hover:bg-[#2DD4BF]/20">
              For businesses ↓
            </a>
            <a href="#project-docs" data-testid="why-docs-jump-projects"
              className="rounded-full border border-[#ECC764]/40 bg-[#ECC764]/10 px-4 py-1.5 text-sm font-bold text-[#ECC764] transition-colors hover:bg-[#ECC764]/20">
              For project financing ↓
            </a>
          </div>

          {/* Explainer video */}
          <div
            data-testid="why-docs-video-wrap"
            className="mx-auto mt-10 max-w-3xl overflow-hidden rounded-2xl border border-white/15 shadow-[0_30px_80px_-20px_rgba(10,139,133,0.45)]"
          >
            <video
              data-testid="why-docs-explainer-video"
              className="aspect-[16/9] w-full object-cover"
              src="/videos/clinq-founder-explainer.mp4"
              poster="/videos/explainer-poster.jpg"
              controls
              playsInline
              preload="metadata"
            />
          </div>
        </div>
      </section>

      {/* Why documents matter */}
      <section className="px-4 py-16 sm:px-6 sm:py-24">
        <div className="mx-auto max-w-3xl">
          <SectionEyebrow testid="why-docs-eyebrow">Why documents matter so much</SectionEyebrow>
          <h2 style={{ fontFamily: "'Raleway', sans-serif" }} className="mt-5 text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
            Funders speak in documents
          </h2>
          <p className="mt-5 text-base leading-relaxed text-neutral-600">
            Before a funder gives you money, they need to answer three questions:{" "}
            <b className="text-neutral-900">Can I trust these numbers? Will this business grow?
            Will I get my money back?</b>{" "}
            They can&apos;t answer those questions from a conversation, however impressive you are in the
            room. They answer them by reading your documents. A funder reviewing hundreds of businesses
            a year makes a fast first judgement: businesses with clear, complete, professional documents
            get a proper look. Businesses without them get a polite &ldquo;come back later.&rdquo; Your
            documents are doing the talking long before you get a meeting — and long after you leave one.
          </p>
          <div
            data-testid="why-docs-founder-callout"
            className="mt-8 rounded-2xl border border-[#0A8B85]/20 bg-[#0A8B85]/[0.05] p-6"
          >
            <p className="text-base leading-relaxed text-neutral-700">
              But here&apos;s what most founders miss: <b className="text-neutral-900">these documents
              aren&apos;t just for funders.</b> Building them properly forces you to understand your own
              business — where the money really goes, what you&apos;re really worth, and what needs
              fixing. Founders who go through this process run better businesses, whether they raise
              capital or not.
            </p>
          </div>
        </div>
      </section>

      {/* Business documents — matches the /for-business Investor Document Checklist */}
      <section id="business-docs" className="bg-neutral-50 px-4 py-16 sm:px-6 sm:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <SectionEyebrow testid="why-docs-business-eyebrow">The Investor Document Checklist — for businesses</SectionEyebrow>
            <h2 style={{ fontFamily: "'Raleway', sans-serif" }} className="mt-5 text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
              Your evidence, piece by piece
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-neutral-600">
              These are the documents funders screen, interrogate and verify before any cheque is
              written — the same checklist Clinq scores your business against.
            </p>
          </div>

          <div className="mt-12 space-y-6">
            {BUSINESS_DOCS.map((d) => (
              <DocCard key={d.n} {...d} testPrefix="doc-card" />
            ))}
          </div>
        </div>
      </section>

      {/* Project financing documents */}
      <section id="project-docs" className="px-4 py-16 sm:px-6 sm:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <SectionEyebrow testid="why-docs-projects-eyebrow">For project financing</SectionEyebrow>
            <h2 style={{ fontFamily: "'Raleway', sans-serif" }} className="mt-5 text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
              Raising for a project? Funders ask for different evidence.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-neutral-600">
              Solar parks, real estate developments, agri-processing, infrastructure — project
              financiers (DFIs, infrastructure funds, banks and private credit) assess the{" "}
              <b className="text-neutral-900">project</b>, not just the company behind it. These six
              documents are what makes a project bankable.
            </p>
          </div>

          <div className="mt-12 space-y-6">
            {PROJECT_DOCS.map((d) => (
              <DocCard key={d.n} {...d} testPrefix="project-doc-card" accent />
            ))}
          </div>

          <div className="mt-10 text-center">
            <button
              data-testid="why-docs-project-cta"
              onClick={() => navigate("/get-started-project")}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#D1A330] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#B8901F] sm:w-auto"
            >
              Score my project free <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-black px-4 py-16 text-white sm:px-6 sm:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 style={{ fontFamily: "'Raleway', sans-serif" }} className="text-2xl font-bold tracking-tight sm:text-3xl">
            Ready businesses get funded
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/75">
            Every funder door that stays closed has the same lock: missing evidence. The documents
            above are the key — and you don&apos;t have to build them alone. Clinq walks you through each
            one, scores your readiness, shows you what to fix, and connects you to funders who match
            your business or project.
          </p>
          <button
            data-testid="why-docs-cta-btn"
            onClick={() => navigate("/get-started")}
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-6 py-3.5 text-base font-bold text-white transition-colors hover:bg-[#066667] sm:w-auto"
          >
            Start Your Capital Readiness Journey <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
